import React from 'react';
import { Modal, Box, Typography, IconButton, Divider, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { Close, Restaurant, Warning } from '@mui/icons-material';
import BasicInfoSection from './BasicInfoSection';
import NutritionInfoSection from './NutritionInfoSection';
import RecommendationsSection from './RecommendationsSection';
import HealthContextSection from './HealthContextSection';
import { useTranslation } from 'react-i18next';
const FoodAnalysisModal = ({ open, onClose, analysisData, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'failed':
        return '#f44336';
      default:
        return '#FF9800';
    }
  };

  if (!analysisData) {
    return null;
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="food-analysis-modal"
      aria-describedby="food-analysis-details"
      BackdropProps={{ onClick: () => {} }}
      dir="auto"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '95%' : '800px',
          maxHeight: '90vh',
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 2,
          // position: 'relative',
          // top: '50%',
          // left: '50%',
          // transform: 'translate(-50%, -50%)',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Restaurant sx={{ color: '#26786A', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              {t('foodAnalysisObj.modal.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#666' }}>
            <Close />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress style={{ color: '#26786a' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <BasicInfoSection analysisData={analysisData} getStatusColor={getStatusColor} formatDate={formatDate} />

            {/* Nutrition Information */}
            {analysisData.nutritionEstimate && (
              <>
                <Divider />
                <NutritionInfoSection nutritionEstimate={analysisData.nutritionEstimate} />
              </>
            )}

            {/* Recommendations */}
            {analysisData.recommendation && (
              <>
                <Divider />
                <RecommendationsSection recommendation={analysisData.recommendation} />
              </>
            )}

            {/* Health Context */}
            {analysisData.healthContextSnapshot && (
              <>
                <Divider />
                <HealthContextSection healthContextSnapshot={analysisData.healthContextSnapshot} />
              </>
            )}

            {/* Error Message */}
            {analysisData.status === 'failed' && analysisData.errorMessage && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Warning sx={{ color: '#f44336', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f44336' }}>
                      {t('foodAnalysisObj.modal.errorTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                      {analysisData.errorMessage}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FoodAnalysisModal;
